import { Component, inject, input } from '@angular/core';
import { IntlDurationPipe } from '@app/shared/pipes/intl-duration.pipe';
import { TimeReportExcelRowMapper } from '@app/shared/services/time-report-excel-row.mapper';
import { ReportColumnsEnum } from '@app/shared/types/models/reports/time-report-panel.model';
import { TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { buildTimeReportExcelWorkbook, DEFAULT_REPORT_EXCEL_COLUMNS } from './time-report-excel.workbook';

const COLUMN_TRANSLATION_KEYS: Partial<Record<ReportColumnsEnum, string>> = {
  [ReportColumnsEnum.DATE]: 'time-reports-export.columns.date',
  [ReportColumnsEnum.MONTH]: 'time-reports-export.columns.month',
  [ReportColumnsEnum.WEEK]: 'time-reports-export.columns.week',
  [ReportColumnsEnum.USER]: 'time-reports-export.columns.user',
  [ReportColumnsEnum.COMPANY]: 'reports-work-time-report-table-desktop-view-company',
  [ReportColumnsEnum.PROJECT]: 'time-reports-export.columns.project',
  [ReportColumnsEnum.TASK_ID]: 'time-reports-export.columns.task-id',
  [ReportColumnsEnum.TASK_LINK]: 'time-reports-export.columns.link',
  [ReportColumnsEnum.TASK_TITLE]: 'time-reports-export.columns.task-title',
  [ReportColumnsEnum.LOCATION]: 'time-reports-export.columns.location',
  [ReportColumnsEnum.DESCRIPTION]: 'time-reports-export.columns.description',
  [ReportColumnsEnum.TYPE]: 'time-reports-export.columns.type',
  [ReportColumnsEnum.TOTAL]: 'time-reports-export.columns.total',
  [ReportColumnsEnum.DECIMAL_HOURS]: 'time-reports-export.columns.decimal-hours',
  [ReportColumnsEnum.STATUS]: 'time-reports-export.columns.status',
  [ReportColumnsEnum.ESTIMATION]: 'time-reports-export.columns.estimation',
  [ReportColumnsEnum.STANDARD]: 'time-reports-export.columns.standard',
  [ReportColumnsEnum.BANK_PLUS]: 'time-reports-export.columns.bank-plus',
  [ReportColumnsEnum.BANK_MINUS]: 'time-reports-export.columns.bank-minus',
  [ReportColumnsEnum.OVERTIME_50]: 'time-reports-export.columns.overtime-50',
  [ReportColumnsEnum.OVERTIME_100]: 'time-reports-export.columns.overtime-100'
};

@Component({
  selector: 'proffeo-time-report-excel-export',
  templateUrl: './time-report-excel-export.component.html',
  imports: [UiButtonComponent]
})
export class TimeReportExcelExportComponent {
  private readonly defaultColumns = DEFAULT_REPORT_EXCEL_COLUMNS;
  private readonly intlDurationPipe = inject(IntlDurationPipe);
  private readonly translateService = inject(TranslateService);
  private readonly excelRowMapper = inject(TimeReportExcelRowMapper);

  public timeReportRows = input.required<TimeReportRowVM[]>();
  public visibleTableColumns = input<ReportColumnsEnum[]>([]);

  protected exportToExcel(): void {
    const inputColumns = this.visibleTableColumns();
    const columns = this.resolveExportColumns(inputColumns.length > 0 ? inputColumns : this.defaultColumns);
    const workbook = buildTimeReportExcelWorkbook({
      excelRows: this.excelRowMapper.mapMany(this.timeReportRows()),
      columns,
      columnLabel: column => this.humanizeColumnName(column),
      formatContext: {
        locale: this.translateService.getCurrentLang() || 'en-US',
        formatDuration: isoDuration => this.intlDurationPipe.transform(isoDuration)
      }
    });

    const fileNamePrefix = this.translateService.instant('time-reports-export.file-name');
    const fileName = fileNamePrefix + '-' + moment().format('DD-MM-YYYY');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  }

  private resolveExportColumns(columns: ReportColumnsEnum[]): ReportColumnsEnum[] {
    if (!columns.includes(ReportColumnsEnum.TASK_ID) || columns.includes(ReportColumnsEnum.TASK_LINK)) {
      return columns;
    }

    const taskIdIndex = columns.indexOf(ReportColumnsEnum.TASK_ID);
    return [...columns.slice(0, taskIdIndex + 1), ReportColumnsEnum.TASK_LINK, ...columns.slice(taskIdIndex + 1)];
  }

  private humanizeColumnName(column: ReportColumnsEnum): string {
    const translationKey = COLUMN_TRANSLATION_KEYS[column];
    return translationKey ? this.translateService.instant(translationKey) : column;
  }
}
